<template>
    <div>
        <div>
            <button
                @disabled="disabled"
                type="button"
                :class="disabled ? 'cursor-not-allowed' : null"
                class="inline-block mt-3"
                @click="
                    disabled ? null : (headingVisibility = !headingVisibility)
                "
            >
                {{ headingVisibility ? "- Remove Heading" : "+ Add Heading" }}
            </button>
        </div>

        <div v-if="headingVisibility" class="mt-3">
            <Select
                v-if="headingVisibility"
                :disabled="disabled"
                label="Heading Type"
                name="heading_type"
                v-model="value.heading_type"
                class="mt-2 col-span-3"
                :options="[
                    { value: 'h3', name: 'h3' },
                    { value: 'h4', name: 'h4' },
                    { value: 'h5', name: 'h5' },
                    { value: 'h6', name: 'h6' },
                ]"
                :error="getError('heading_type')"
            />
            <input-text
                :disabled="disabled"
                v-if="headingVisibility"
                label="Heading Title"
                name="heading_title"
                v-model="value.heading_title"
                :error="getError('heading_title')"
                class="mt-3"
            />
            <input-text
                :disabled="disabled"
                v-if="headingVisibility"
                label="Anchor for Heading Title (excluding #)"
                name="heading_anchor"
                v-model="value.heading_anchor"
                :error="getError('heading_anchor')"
                class="mt-3"
                :maxlength="255"
            />
            <input-text
                :disabled="disabled"
                name="heading_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                class="mt-3"
                v-model="value.heading_anchor_text"
                :error="
                    errors.heading_anchor_text
                        ? errors.heading_anchor_text[0]
                        : null
                "
            />
        </div>

        <input-rich-text
            :disabled="disabled"
            v-if="elements.content.visibility"
            :id="`content_block_${contentBlockIndex}_section_${index}`"
            label="Content"
            name="content"
            :language="language"
            v-model="value.content"
            editor="content"
        ></input-rich-text>

        <Select
            :disabled="disabled"
            v-if="elements.content.visibility"
            label="Add Trust Badge"
            name="content_trust_badge"
            class="mt-3"
            v-model="value.content_trust_badge"
            :options="[
                        { value: '', name: 'No Badge'},
                        { value: 'verified-experts', name: 'Verified experts for'},
                        { value: 'safety-checked', name: 'Safety checked by'},
                        { value: 'approved', name: 'Approved by'},
                        { value: 'tested', name: 'Tested by'},
                    ]"
            :error="getError('content_trust_badge')"
        />

        <div
            v-if="elements.conversions.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Conversion (one operator)</p>
            <multi-reference
                create-text="Create new conversion and link conversion to this page"
                link-text="Link existing conversions"
                :disabled="disabled"
                v-model="value.conversions"
                :linkable="false"
                :editable="true"
                :language="language"
                field-name="conversions"
                model-data="Conversion"
                :max-items="10"
                max-item-message="You can only add a max of 10 conversions"
            />

            <input-checkbox
                name="hide_usps"
                label="Hide USPs?"
                class="mt-3"
                v-model="value.hide_usps"
                :error="errors.hide_usps ? errors.hide_usps[0] : null"
            />
        </div>

        <div
            v-if="elements.image.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <media-reference
                :disabled="disabled"
                :feeds-into-gallery="true"
                :language="language"
                :gallery="gallery"
                v-model="value.image"
                class="mt-3"
                label="Image"
                name="image"
                @add-to-gallery="
                    (file) => {
                        this.imageIncludedInLibrary = true;
                        $emit('add-to-gallery', file);
                    }
                "
                @remove-from-gallery="
                    (file) => {
                        this.imageIncludedInLibrary = false;
                        $emit('remove-from-gallery', file);
                    }
                "
            />

            <rocket-link
                :language="language"
                v-model="value.link"
                class="mt-5"
                label="Image Link"
                :hide-link-text="true"
                :resettable="true"
            />
        </div>

        <div
            v-if="elements.table.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <rocket-table
                :language="language"
                v-model="value.table"
                :errors="getElementErrors('table').root"
            />
        </div>

        <latest-articles
            v-if="elements.latestArticles.visibility"
            v-model="value.latest_articles_element"
            :language="language"
            :error-bag="getElementErrors('latest_articles_element')"
        />

        <ordered-list
            v-if="elements.orderedList.visibility"
            v-model="value.ordered_list"
            :language="language"
            :error-bag="errors"
            :content-block-id="this.contentBlockIndex"
            :content-section-id="this.index"
        />

        <list-with-images
            v-if="elements.listWithImages.visibility"
            v-model="value.list_with_images"
            :language="language"
            :error-bag="errors"
        />

        <simple-list-with-images
            v-if="elements.simpleListWithImages.visibility"
            v-model="value.simple_list_with_images"
            :language="language"
            :error-bag="errors"
            :content-block-id="this.contentBlockIndex"
            :content-section-id="this.index"
        />

        <trust-banner
            v-if="elements.trustBanner.visibility"
            v-model="value.trust_banner"
            :language="language"
            :error-bag="errors"
            :content-block-id="this.contentBlockIndex"
            :content-section-id="this.index"
        />

        <author-quote
            v-if="elements.authorQuote.visibility"
            v-model="value.author_quote"
            :language="language"
            :error-bag="getElementErrors('author_quote')"
            :content-block-id="this.contentBlockIndex"
            :content-section-id="this.index"
        />

        <slider
            v-if="elements.slider.visibility"
            v-model="value.slider"
            :language="language"
            :error-bag="getElementErrors('slider')"
            :content-block-id="this.contentBlockIndex"
            :content-section-id="this.index"
        />

        <video-element
            v-if="elements.video.visibility"
            v-model="value.video"
            :language="language"
            :error-bag="errors"
            :content-block-id="this.contentBlockIndex"
            :content-section-id="this.index"
        />

        <list-with-background-image
            v-if="elements.listWithBackgroundImage.visibility"
            v-model="value.list_with_background_image"
            :language="language"
            :error-bag="errors"
            :content-block-id="this.contentBlockIndex"
            :content-section-id="this.index"
        />

        <div
            v-if="elements.alertBoxes.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Alert Box</p>
            <multi-reference
                create-text="Create new alert box and link alert box to this page"
                link-text="Link existing alert boxes"
                :disabled="disabled"
                :language="language"
                :linkable="false"
                :editable="true"
                v-model="value.alert_boxes"
                field-name="alertBoxes"
                model-data="Alert Box"
            />
        </div>

        <div
            v-if="elements.faq.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">FAQ</p>
            <reference
                create-text="Create new FAQ and link FAQ to this page"
                link-text="Link an existing FAQ"
                :disabled="disabled"
                :linkable="true"
                :language="language"
                :editable="true"
                v-model="value.faq"
                model-data="FAQ"
                :should-open-new-window="true"
            />
        </div>

        <div
            v-if="elements.testimonial.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Testimonial</p>
            <reference
                create-text="Create new Testimonial and link Testimonial to this page"
                link-text="Link an existing Testimonial"
                :disabled="disabled"
                :linkable="true"
                :language="language"
                :editable="true"
                v-model="value.testimonial"
                model-data="Testimonial"
                :should-open-new-window="true"
            />
        </div>

        <div
            v-if="elements.jump_navi_with_author_quote.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Jump navigation with Author Quote</p>
            <reference
                create-text="Create new jump navigation with author quote and link it to this page"
                link-text="Link an existing jump navigation with author quote"
                :disabled="disabled"
                :linkable="false"
                :editable="true"
                :language="language"
                v-model="value.jump_navi_with_author_quote"
                model-data="Jump Navi With Author Quote"
            />
        </div>

        <div
            v-if="elements.ranking.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Ranking</p>
            <reference
                create-text="Create new ranking and link ranking to this page"
                link-text="Link an existing ranking"
                :disabled="disabled"
                :linkable="true"
                :creatable="false"
                :should-open-new-window="true"
                :language="language"
                :order="{
                    name: 'asc',
                }"
                v-model="value.ranking"
                model-data="Ranking"
            />
        </div>

        <div
            v-if="elements.bonus_list_top.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Bonus List Top</p>

            <bonus-list
                :language="language"
                v-model="value.bonus_list_top"
                :document-class="documentClass"
                :document-data-id="documentDataId"
                :document-type-id="documentTypeId"
            />
        </div>

        <div
            v-if="elements.bonus_list_bottom.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200">Bonus List Bottom</p>
        </div>

        <div
            v-if="elements.slot_ranking.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Slot Ranking</p>
            <reference
                create-text="Create new slot ranking and link slot ranking to this page"
                link-text="Link an existing slot ranking"
                :disabled="disabled"
                :linkable="true"
                :creatable="false"
                :should-open-new-window="true"
                :query="{
                    language_id: language.id,
                }"
                :order="{
                    name: 'asc',
                }"
                v-model="value.slot_ranking"
                model-data="Slot Ranking"
            />
        </div>

        <div
            v-if="elements.similarSlotReviews.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Slot Reviews</p>
            <multi-reference
                create-text="Create new slot review and link slot review to this page"
                link-text="Link existing slot reviews"
                :disabled="disabled"
                :creatable="false"
                :language="language"
                v-model="value.similar_slot_reviews"
                :should-open-new-window="true"
                :query="{
                    has_valid_bonuses: true,
                    status_id: 1,
                }"
                :order="{
                    'slot_reviews.name': 'asc',
                }"
                field-name="similarSlotReviews"
                model-data="Slot Review"
            />
        </div>

        <div
            v-if="elements.similarGameReviews.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Game Reviews</p>
            <multi-reference
                create-text="Create new game review and link game review to this page"
                link-text="Link existing game reviews"
                :disabled="disabled"
                :creatable="false"
                :language="language"
                v-model="value.similar_game_reviews"
                :should-open-new-window="true"
                :query="{
                    has_valid_bonuses: true,
                    status_id: 1,
                }"
                :order="{
                    'game_reviews.name': 'asc',
                }"
                field-name="similarGameReviews"
                model-data="Game Review"
            />
        </div>

        <div
            v-if="elements.ourExperts.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Our Experts</p>
            <multi-reference
                create-text="Create new author and link author to this page"
                link-text="Link existing authors"
                :disabled="disabled"
                :creatable="false"
                :language="language"
                v-model="value.our_experts"
                :should-open-new-window="true"
                :query="{
                    status_id: 1,
                }"
                :order="{
                    'authors.name': 'asc',
                }"
                field-name="ourExperts"
                model-data="Author"
            />
        </div>

        <div
            v-if="elements.casinoReviews.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Casino Reviews</p>

            <multi-reference
                create-text="Create new casino review and link casino review to this page"
                link-text="Link existing casino reviews"
                :disabled="disabled"
                :creatable="false"
                :language="language"
                v-model="value.casino_reviews"
                :should-open-new-window="true"
                :query="{
                    has_valid_bonuses: true,
                    status_id: 1,
                }"
                :order="{
                    'casino_reviews.name': 'asc'
                }"
                field-name="casinoReviews"
                model-data="Casino Review"
            />
        </div>

        <div
            v-if="elements.software_table_with_big_list.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Software Table with Big List</p>
            <table-with-big-list
                :language-id="language.id"
                model="Software"
                v-model="value.software_table_with_big_list"
                :contentBlockIndex="contentBlockIndex"
            />
        </div>

        <div
            v-if="elements.license_table_with_big_list.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">License Table with Big List</p>
            <table-with-big-list
                :language-id="language.id"
                model="License"
                v-model="value.license_table_with_big_list"
                :contentBlockIndex="contentBlockIndex"
            />
        </div>

        <div
            v-if="elements.payment_method_table_with_big_list.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Payment Method Table with Big List</p>

            <table-with-big-list-options
                :language-id="language.id"
                model="Payment Method"
                v-model="value.payment_method_table_with_big_list_options"
                :contentBlockIndex="contentBlockIndex"
                :is-payment-category-page="isPaymentCategoryPage"
                :payment-method-id="paymentMethodId"
                />

            <table-with-big-list
                :language-id="language.id"
                model="Payment Method"
                v-model="value.payment_method_table_with_big_list"
                :contentBlockIndex="contentBlockIndex"
                :should-show-reset-button="false"
                :list-options="value.payment_method_table_with_big_list_options"
            />
        </div>

        <div
            v-if="elements.content_table.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">RichText Table</p>
            <table-builder
                :language="language"
                :gallery="gallery"
                :disabled="disabled"
                v-model="value.content_table"
                :content-block-id="this.contentBlockIndex"
                :content-section-id="this.index"
            />
        </div>

        <div
            v-if="elements.deposit_withdrawal_methods_and_currencies.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200">
                Deposit methods, withdrawal methods and currencies ✅
            </p>
        </div>

        <div
            v-if="elements.show_market_data.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200">
                Available Languages, Main Countries and Excluded Countries ✅
            </p>
        </div>

        <div
            v-if="elements.show_additional_casinos.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200">
                {{additionalCasinosComponent.name}} ✅
            </p>
        </div>

        <div
            v-if="elements.list_of_available_games.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200">List of available games ✅</p>
        </div>

        <div
            v-if="elements.list_of_available_languages.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200">List of available languages ✅</p>
        </div>

        <div
            v-if="elements.list_of_not_recommended_casinos.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200">List of not recommended casinos ✅</p>
        </div>

        <div
            v-if="elements.list_of_out_of_business_casinos.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200">List of out of business casinos ✅</p>
        </div>

        <div
            v-if="elements.more_on_this_topic.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">More on this topic</p>
            <rocket-links
                :language="language"
                class="mb-3 pl-5"
                v-model="value.more_on_this_topic"
                :disabled="disabled"
            />
        </div>

        <div
            v-if="elements.pros_and_cons.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Pros and Cons</p>
            <reference
                create-text="Create new pros and cons and link pros and cons to this page"
                link-text="Link an existing pros and cons"
                :disabled="disabled"
                :editable="true"
                :linkable="false"
                :language="language"
                v-model="value.pros_and_cons"
                :should-open-new-window="true"
                model-data="Pros and Cons"
            />
        </div>

        <div
            v-if="elements.bonus_tiles.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">* Bonuses (Max. 4)</p>

            <multi-reference
                create-text="Create new bonus and link bonus to this page"
                link-text="Link existing bonuses"
                :linkable="false"
                :editable="true"
                :language="language"
                :max-items="4"
                v-model="value.bonus_tiles"
                field-name="bonus_tiles"
                model-data="Bonus Tile"
                :error="getError('bonus_tiles')"
            />
        </div>

        <div
            v-if="elements.how_to.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">How To</p>

            <reference
                create-text="Create new how to and link how to to this page"
                link-text="Link an existing how to"
                :disabled="disabled"
                v-model="value.how_to"
                :editable="true"
                :language="language"
                field-name="how_to"
                model-data="How To"
                :should-open-new-window="true"
            />
        </div>

        <div
            v-if="elements.category_tiles.visibility"
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Category Tiles</p>

            <multi-reference
                create-text="Create new category tile and link category tile to this page"
                link-text="Link existing category tiles"
                :linkable="false"
                :editable="true"
                v-model="value.category_tiles"
                model-data="Category Tile"
                field-name="category_tiles"
                :language="language"
                :error="getError('category_tiles')"
            />
        </div>

        <div
            v-if="elements.reference_list.visibility"
        >
            <reference-list
                :language="language"
                v-model="value.reference_list"
                :error-bag="getElementErrors('reference_list')"
            />
        </div>

        <timeline
            v-if="elements.timeline.visibility"
            v-model="value.timeline"
            :language="language"
            :error-bag="getElementErrors('timeline')"
        />

        <top3-operators
            v-if="elements.top3Operators.visibility"
            v-model="value.top3_operators"
            :language="language"
            :error-bag="getElementErrors('top3_operators')"
        />

        <multi-reference
            :list-data="elementMultiReferenceData"
            @input="elementSelectionChanged"
            class="mt-3"
            :disabled="disabled"
            :class="disabled ? 'cursor-not-allowed' : null"
            link-text="+ Add Element"
            :max-items="2"
            :editable="false"
            :creatable="false"
            :hide-link-icon="true"
            v-model="selectedElements"
            field-name="elements"
        />
    </div>
</template>

<script>
import EventBus from "../../eventBus";
import siteLanguage from '../../config/siteLanguage'
import { v4 as uuidv4 } from "uuid";
import OrderedList from "../ContentBlockElements/OrderedList.vue";

const MAX_ITEMS = 9

export default {
    name: "ContentSection",
    components: {OrderedList},
    props: {
        value: Object,
        index: Number,
        contentBlockIndex: Number,
        documentClass: {
            type: String,
            required: true,
        },
        documentId: {
            type: Number,
            required: false,
        },
        documentDataId: {
            type: Number,
            required: false,
        },
        documentTypeId: {
            type: Number,
            required: false,
        },
        hasBonusLists: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasRanking: {
            type: Boolean,
            required: false,
            default: true,
        },
        gallery: Array,
        shouldShowMarketData: {
           type : Boolean,
           default : false
        },
        additionalCasinosComponent: {
            type: Object,
            default: () => {
                return { shouldShow: false };
            },
        },
        isPaymentCategoryPage: {
            type: Boolean,
            default: false,
        },
        paymentMethodId: {
            type: Number,
            required: false,
        },
        errors: {
            type: [Array, Object],
            required: true,
        },
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        contentSectionWithHowTo: {
            type: String,
            required: false,
        },
        contentSectionWithBonusListTop: {
            type: String,
            required: false,
        },
        contentSectionWithBonusListBottom: {
            type: String,
            required: false,
        },
        contentSectionsWithRanking: {
            type: Array,
            required: false,
        },
    },
    data() {
        return {
            listOpen: false,
            selectedElements: [],
            imageIncludedInLibrary: false,
            headingVisibility: false,
            elements: {
                content: {
                    visibility: false,
                    name: "Content",
                    enabled: true,
                },
                conversions: {
                    visibility: false,
                    name: "Conversion (one operator)",
                    enabled: true,
                },
                ranking: {
                    visibility: false,
                    name: "Ranking",
                    enabled: true,
                },
                bonus_list_top: {
                    visibility: false,
                    name: "Bonus List Top",
                    enabled: true,
                },
                bonus_list_bottom: {
                    type: "boolean",
                    visibility: false,
                    name: "Bonus List Bottom",
                    enabled: true,
                },
                slot_ranking: {
                    visibility: false,
                    name: "Slot Ranking",
                    enabled: true,
                },
                similarSlotReviews: {
                    visibility: false,
                    name: "Slot Reviews",
                    enabled: true,
                },
                similarGameReviews: {
                    visibility: false,
                    name: "Game Reviews",
                    enabled: true,
                },
                ourExperts: {
                    visibility: false,
                    name: "Our Experts",
                    enabled: true,
                },
                casinoReviews: {
                    visibility: false,
                    name: "Casino Reviews",
                    enabled: true,
                },
                image: {
                    visibility: false,
                    name: "Image",
                    enabled: true,
                },
                latestArticles: {
                    visibility: false,
                    name: "Latest Articles",
                    enabled: true,
                },
                orderedList: {
                    visibility: false,
                    name: "List with Icons / Numbers",
                    enabled: true,
                },
                listWithImages: {
                    visibility: false,
                    name: "List with Images",
                    enabled: true,
                },
                simpleListWithImages: {
                    visibility: false,
                    name: "Simple List with Images",
                    enabled: true,
                },
                trustBanner: {
                    visibility: false,
                    name: "Trust Banner",
                    enabled: true,
                },
                authorQuote: {
                    visibility: false,
                    name: "Author Quote",
                    enabled: true,
                },
                slider: {
                    visibility: false,
                    name: "Slider",
                    enabled: true,
                },
                video: {
                    visibility: false,
                    name: "Video",
                    enabled: true,
                },
                listWithBackgroundImage: {
                    visibility: false,
                    name: "List with Background Image",
                    enabled: true,
                },
                jump_navi_with_author_quote: {
                    visibility: false,
                    name: "Jump navigation with Author Quote",
                    enabled: true,
                },
                alertBoxes: {
                    visibility: false,
                    name: "Alert Box",
                    enabled: true,
                },
                faq: {
                    visibility: false,
                    name: "FAQs",
                    enabled: true,
                },
                testimonial: {
                    visibility: false,
                    name: "Testimonial",
                    enabled: true,
                },
                software_table_with_big_list: {
                    visibility: false,
                    name: "Software table with big list",
                    enabled: true,
                },
                license_table_with_big_list: {
                    visibility: false,
                    name: "License table with big list",
                    enabled: true,
                },
                payment_method_table_with_big_list: {
                    visibility: false,
                    name: "Payment method table with big list",
                    enabled: true,
                },
                content_table: {
                    visibility: false,
                    name: "RichText Table",
                    enabled: true,
                },
                deposit_withdrawal_methods_and_currencies: {
                    type: "boolean",
                    visibility: false,
                    name: "Deposit methods, withdrawal methods and currencies",
                    enabled: true,
                },
                show_market_data: {
                    type: "boolean",
                    visibility: false,
                    name: "Available languages, main countries and excluded countries",
                    enabled: this.shouldShowMarketData,
                },
                show_additional_casinos: {
                    type: "boolean",
                    visibility: false,
                    name: this.additionalCasinosComponent.name,
                    enabled: this.additionalCasinosComponent.shouldShow,
                },
                list_of_available_games: {
                    type: "boolean",
                    visibility: false,
                    name: "List of available games",
                    enabled: true,
                },
                list_of_available_languages: {
                    type: "boolean",
                    visibility: false,
                    name: "List of available languages",
                    enabled: true,
                },
                list_of_not_recommended_casinos: {
                    type: "boolean",
                    visibility: false,
                    name: "List of not recommended casinos",
                    enabled: true,
                },
                list_of_out_of_business_casinos: {
                    type: "boolean",
                    visibility: false,
                    name: "List of out of business casinos",
                    enabled: true,
                },
                more_on_this_topic: {
                    visibility: false,
                    name: "More on this topic",
                    enabled: true,
                },
                table: {
                    visibility: false,
                    name: "Table",
                    enabled: true,
                },
                pros_and_cons: {
                    visibility: false,
                    name: "Pros and Cons",
                    enabled: true,
                },
                bonus_tiles: {
                    visibility: false,
                    name: "Bonus Tiles",
                    enabled: true,
                },
                how_to: {
                    visibility: false,
                    name: "How To",
                    enabled: true,
                },
                category_tiles: {
                    visibility: false,
                    name: "Category Tiles",
                    enabled: true,
                },
                reference_list: {
                    visibility: false,
                    name: "Reference List",
                    enabled: true,
                },
                timeline: {
                    visibility: false,
                    name: "Timeline",
                    enabled: true,
                },
                top3Operators: {
                    visibility: false,
                    name: "Top3 Operators",
                    enabled: true,
                },

            },
        };
    },
    computed: {
        enabledElements: function () {
            const elements = this.elements;
            return Object.keys(elements).reduce(function (
                enabledElements,
                name
            ) {
                if (elements[name].enabled)
                    enabledElements[name] = elements[name];
                return enabledElements;
            },
            {});
        },
        elementMultiReferenceData: function () {
            let elements = [];
            Object.keys(this.enabledElements).forEach(key => {
                elements.push({
                    id: key,
                    name: this.enabledElements[key].name,
                });
            });
            elements.sort((a, b) => {
                if (a.name < b.name) {
                    return -1;
                }
                if (a.name > b.name) {
                    return 1;
                }
                return 0;
            });
            return elements;
        },
    },
    mounted() {
        this.value.contentSectionUuid = uuidv4();
        this.elements.how_to.enabled = (this.contentSectionWithHowTo == null || this.contentSectionWithHowTo === this.value.contentSectionUuid);
        this.setBonusListsAndRankingEnabled();

        this.checkVisibility();
    },
    methods: {
        setBonusListsAndRankingEnabled() {
            this.elements.bonus_list_top.enabled = this.hasBonusLists && this.documentClass !== 'Category' // Category has a standalone BonusListTop element
                && !this.contentSectionsWithRanking.length
                && (this.contentSectionWithBonusListTop == null || this.contentSectionWithBonusListTop === this.value.contentSectionUuid);

            this.elements.bonus_list_bottom.enabled = this.hasBonusLists // category page
                && !this.contentSectionsWithRanking.length
                && (this.contentSectionWithBonusListBottom == null || this.contentSectionWithBonusListBottom === this.value.contentSectionUuid);

            this.elements.ranking.enabled = this.hasRanking // category page
                && this.contentSectionWithBonusListTop == null
                && this.contentSectionWithBonusListBottom == null
                && ((this.contentSectionsWithRanking.length < 2 && this.documentClass !== 'Category')
                    || (this.contentSectionsWithRanking.length < 1 && this.documentClass === 'Category')
                    || this.contentSectionsWithRanking.indexOf(this.value.contentSectionUuid) >= 0);
        },
        elementSelectionChanged(elements) {
            // reset all elements visibility to false first
            Object.keys(this.elements).forEach(name => {
                this.elements[name].visibility = false;
            });

            // set selected to visible
            elements.forEach(element => {
                if (element.id === 'table' && this.value.table === null) {
                    this.value.table = {
                        id: null,
                        type_id: null,
                        heading_text: null,
                        heading_type: null,
                        anchor_id: null,
                        anchor_text: null,
                        document: null,
                        document_type: null,
                        document_id: null,
                        bonus: null,
                        bonus_id: null,
                        image: null,
                        image_id: null,
                        language_id: null,
                        headers: [],
                        rows: [],
                    };
                }

                this.elements[element.id].visibility = true;
            });
        },
        checkVisibility() {
            this.elements.content.visibility = !!this.value.content || !!this.value.content_trust_badge;
            this.elements.conversions.visibility =
                (this.value.conversions || []).length !== 0;
            this.elements.image.visibility = !!this.value.image;
            this.elements.latestArticles.visibility = this.value.latest_articles_element?.hasOwnProperty('document_type');
            this.elements.alertBoxes.visibility =
                (this.value.alert_boxes || []).length !== 0;
            this.elements.orderedList.visibility = this.value?.ordered_list?.hasOwnProperty('type');
            this.elements.listWithImages.visibility =
                (this.value.list_with_images || []).length !== 0;
            this.elements.simpleListWithImages.visibility =
                (this.value.simple_list_with_images || []).length !== 0;
            this.elements.trustBanner.visibility =
                (this.value.trust_banner || []).length !== 0;
            this.elements.authorQuote.visibility =
                (this.value.author_quote || []).length !== 0;
            this.elements.slider.visibility =
                (this.value.slider || []).length !== 0;
            this.elements.video.visibility = !!this.value.video;
            this.elements.timeline.visibility =
                (this.value.timeline || []).length !== 0;
            this.elements.top3Operators.visibility =
                (this.value.top3_operators || []).length !== 0;
            this.elements.listWithBackgroundImage.visibility =
                (this.value.list_with_background_image || []).length !== 0;
            this.elements.faq.visibility = !!this.value?.faq?.heading;
            this.elements.testimonial.visibility = (this.value.testimonial || []).length !== 0;
            this.elements.jump_navi_with_author_quote.visibility = !!this.value
                ?.jump_navi_with_author_quote?.name;
            this.elements.software_table_with_big_list.visibility =
                (this.value.software_table_with_big_list || []).length !== 0;
            this.elements.license_table_with_big_list.visibility =
                (this.value.license_table_with_big_list || []).length !== 0;
            this.elements.payment_method_table_with_big_list.visibility =
                (this.value.payment_method_table_with_big_list || []).length !==
                0;
            this.elements.content_table.visibility = !!this.value.content_table
                ?.table_markup;
            this.elements.deposit_withdrawal_methods_and_currencies.visibility = this.value.deposit_withdrawal_methods_and_currencies;
            this.elements.show_market_data.visibility = this.value.show_market_data;
            this.elements.show_additional_casinos.visibility = this.value.show_additional_casinos;
            this.elements.list_of_available_games.visibility = this.value.list_of_available_games;
            this.elements.list_of_available_languages.visibility = this.value.list_of_available_languages;
            this.elements.list_of_not_recommended_casinos.visibility = this.value.list_of_not_recommended_casinos;
            this.elements.list_of_out_of_business_casinos.visibility = this.value.list_of_out_of_business_casinos;
            this.elements.ranking.visibility = !!this.value?.ranking?.name;
            this.elements.bonus_list_top.visibility = (this.value?.bonus_list_top?.bonusList || []).length !== 0 || this.value?.bonus_list_top?.listOptions.top_list_count > 0;
            this.elements.bonus_list_bottom.visibility = this.value.bonus_list_bottom;
            this.elements.slot_ranking.visibility = !!this.value?.slot_ranking
                ?.name;
            this.elements.similarSlotReviews.visibility =
                (this.value.similar_slot_reviews || []).length !== 0;
            this.elements.similarGameReviews.visibility =
                (this.value.similar_game_reviews || []).length !== 0;
            this.elements.ourExperts.visibility =
                (this.value.our_experts || []).length !== 0;
            this.elements.casinoReviews.visibility =
                (this.value.casino_reviews || []).length !== 0;
            this.elements.more_on_this_topic.visibility =
                (this.value.more_on_this_topic || []).length !== 0;
            this.elements.table.visibility = !!this.value.table?.type_id;
            this.elements.pros_and_cons.visibility = !!this.value?.pros_and_cons
                ?.name;
            this.elements.bonus_tiles.visibility =
                (this.value.bonus_tiles || []).length !== 0;
            this.elements.how_to.visibility =
                (this.value.how_to || []).length !== 0;
            this.elements.category_tiles.visibility =
                (this.value.category_tiles || []).length !== 0;

            this.elements.reference_list.visibility = !!this.value.reference_list?.type;

            this.headingVisibility = !!this.value.heading_title;

            // set selectedElements to populate multireference
            this.selectedElements = [];
            Object.keys(this.elements).forEach(key => {
                if(this.elements[key].visibility) {
                    this.selectedElements.push({
                        id: key,
                        name: this.elements[key].name,
                    })
                }
            });
        },
        getError(field) {
            let error_key = [
                "contentBlocks",
                this.contentBlockIndex,
                "sections",
                this.index,
                field,
            ].join(".");
            return this.errors[error_key] ? this.errors[error_key][0] : null;
        },
        getElementErrors(element) {
            let errorKey = [
                "contentBlocks",
                this.contentBlockIndex,
                "sections",
                this.index,
                element,
            ].join(".");

            let elementErrors = {};

            for (const key of Object.keys(this.errors)) {
                if (key.startsWith(errorKey)) {
                    let shortIndex = key.replace(errorKey, '');

                    if (shortIndex.startsWith('.')) {
                        shortIndex = shortIndex.replace(/^\./gi, '');
                    }
                    else if (shortIndex === '') {
                        // example: contentBlocks.0.sections.0.reference_list, which will become an empty string, 'root' is a fallback key for such cases
                        shortIndex = 'root';
                    }
                    elementErrors[shortIndex] = this.errors[key];
                }
            }

            return elementErrors;
        },
    },
    watch: {
        contentSectionWithHowTo: function (newValue) {
            this.elements.how_to.enabled = (this.contentSectionWithHowTo == null || this.contentSectionWithHowTo === this.value.contentSectionUuid);
        },
        hasBonusLists: function (newValue) {
            this.setBonusListsAndRankingEnabled();
        },
        hasRanking: function (newValue) {
            this.setBonusListsAndRankingEnabled();
        },
        contentSectionWithBonusListTop: function (newValue) {
            this.setBonusListsAndRankingEnabled();
        },
        contentSectionWithBonusListBottom: function (newValue) {
            this.setBonusListsAndRankingEnabled();
        },
        contentSectionsWithRanking: function (newValue) {
            this.setBonusListsAndRankingEnabled();
        },
        "value.ranking": function (newValue) {
            this.value.ranking_id = newValue ? newValue.id : null;
        },
        "value.slot_ranking": function (newValue) {
            this.value.slot_ranking_id = newValue ? newValue.id : null;
        },
        "value.pros_and_cons": function (newValue) {
            this.value.pros_and_cons_list_id = newValue ? newValue.id : null;
        },
        "value.how_to": function (newValue) {
            this.value.how_to_id = newValue ? newValue.id : null;
        },
        headingVisibility: function (visibility) {
            if (!visibility) {
                this.value.heading_type = null;
                this.value.heading_title = null;
                this.value.heading_anchor = null;
                this.value.heading_anchor_text = null;
            } else {
                if (!this.value.heading_type) {
                    this.value.heading_type = "h3";
                }
            }
        },
        "elements.ranking.visibility": function (visibility, oldVisibility) {
            if (!visibility) {
                this.value.ranking = null;
            }

            if (visibility != oldVisibility) {
                this.$emit(visibility ? 'add-ranking' : 'delete-ranking', this.value.contentSectionUuid);
            }
        },
        "elements.bonus_list_top.visibility": function (visibility, oldVisibility) {
            if (!visibility) {
                this.value.bonus_list_top = null;
            }

            if (visibility != oldVisibility) {
                this.$emit(visibility ? 'add-bonus-list-top' : 'delete-bonus-list-top', this.value.contentSectionUuid);
            }
        },
        "elements.bonus_list_bottom.visibility": function (visibility, oldVisibility) {
            this.value.bonus_list_bottom = visibility;

            if (visibility != oldVisibility) {
                this.$emit(visibility ? 'add-bonus-list-bottom' : 'delete-bonus-list-bottom', this.value.contentSectionUuid);
            }
        },
        "elements.slot_ranking.visibility": function (visibility) {
            if (!visibility) {
                this.value.slot_ranking = null;
            }
        },
        "elements.deposit_withdrawal_methods_and_currencies.visibility": function (
            newValue
        ) {
            this.value.deposit_withdrawal_methods_and_currencies = newValue;
        },
        "elements.show_market_data.visibility": function (
            newValue
        ) {
            this.value.show_market_data = newValue;
        },
        "elements.latestArticles.visibility": function (
            visible
        ) {
            if (!visible) {
                this.value.latest_articles_element = {};
            }
        },
        "elements.show_additional_casinos.visibility": function (
            newValue
        ) {
            this.value.show_additional_casinos = newValue;
        },
        "elements.list_of_available_games.visibility": function (newValue) {
            this.value.list_of_available_games = newValue;
        },
        "elements.list_of_available_languages.visibility": function (newValue) {
            this.value.list_of_available_languages = newValue;
        },
        "elements.list_of_not_recommended_casinos.visibility": function (
            newValue
        ) {
            this.value.list_of_not_recommended_casinos = newValue;
        },
        "elements.list_of_out_of_business_casinos.visibility": function (
            newValue
        ) {
            this.value.list_of_out_of_business_casinos = newValue;
        },

        "elements.content.visibility": function (visibility) {
            if (!visibility) {
                this.value.content = null;
                this.value.content_trust_badge = null;
            }
        },
        "elements.conversions.visibility": function (visibility) {
            if (!visibility) {
                this.value.conversions = [];
                this.value.hide_usps = false;
            }
        },
        "elements.image.visibility": function (visibility) {
            if (!visibility) {
                EventBus.$emit("remove-from-gallery", this.value.image);
                this.value.image = null;
                this.value.image_id = null;
            }
        },
        "value.image": function (newVal, oldVal) {
            if (oldVal) {
                EventBus.$emit("remove-from-gallery", oldVal);
            }
            if (newVal && this.imageIncludedInLibrary) {
                EventBus.$emit("add-to-gallery", newVal);
            }
        },
        "elements.orderedList.visibility": function (visibility) {
            if (!visibility) {
                this.value.ordered_list = [];
            }
        },
        "elements.listWithImages.visibility": function (visibility) {
            if (!visibility) {
                this.value.list_with_images = [];
            }
        },
        "elements.simpleListWithImages.visibility": function (visibility) {
            if (!visibility) {
                this.value.simple_list_with_images = [];
            }
        },
        "elements.trustBanner.visibility": function (visibility) {
            if (!visibility) {
                this.value.trust_banner = null;
            }
        },
        "elements.authorQuote.visibility": function (visibility) {
            if (!visibility) {
                this.value.author_quote = null;
            }
        },
        "elements.slider.visibility": function (visibility) {
            if (!visibility) {
                this.value.slider = null;
            }
        },
        "elements.video.visibility": function (visibility) {
            if (!visibility) {
                this.value.video = null;
            }
        },
        "elements.timeline.visibility": function (visibility) {
            if (!visibility) {
                this.value.timeline = null;
            }
        },

        "elements.top3Operators.visibility": function (visibility) {
            if (!visibility) {
                this.value.top3_operators = null;
            }
        },
        "elements.listWithBackgroundImage.visibility": function (visibility) {
            if (!visibility) {
                this.value.list_with_background_image = [];
            }
        },
        "elements.alertBoxes.visibility": function (visibility) {
            if (!visibility) {
                this.value.alert_boxes = [];
            }
        },
        "elements.faq.visibility": function (visibility) {
            if (!visibility) {
                this.value.faq = null;
                this.value.faq_id = null;
            }
        },
        "elements.testimonial.visibility": function (visibility) {
            if (!visibility) {
                this.value.testimonial = null;
                this.value.testimonial_id = null;
            }
        },
        "elements.jump_navi_with_author_quote.visibility": function (
            visibility
        ) {
            if (!visibility) {
                this.value.jump_navi_with_author_quote = null;
                this.value.jump_navi_with_author_quote_id = null;
            }
        },
        "elements.software_table_with_big_list.visibility": function (
            visibility
        ) {
            if (!visibility) {
                this.value.software_table_with_big_list = [];
            }
        },
        "elements.license_table_with_big_list.visibility": function (
            visibility
        ) {
            if (!visibility) {
                this.value.license_table_with_big_list = [];
            }
        },
        "elements.payment_method_table_with_big_list.visibility": function (
            visibility
        ) {
            if (!visibility) {
                this.value.payment_method_table_with_big_list = [];
                this.value.payment_method_table_with_big_list_options = {};
            }
        },
        "elements.content_table.visibility": function (visibility) {
            if (!visibility) {
                this.value.content_table = null;
            }
        },
        "elements.similarSlotReviews.visibility": function (visibility) {
            if (!visibility) {
                this.value.similar_slot_reviews = [];
            }
        },
        "elements.similarGameReviews.visibility": function (visibility) {
            if (!visibility) {
                this.value.similar_game_reviews = [];
            }
        },
        "elements.ourExperts.visibility": function (visibility) {
            if (!visibility) {
                this.value.our_experts = [];
            }
        },
        "elements.casinoReviews.visibility": function (visibility) {
            if (!visibility) {
                this.value.casino_reviews = [];
            }
        },
        "elements.more_on_this_topic.visibility": function (visibility) {
            if (!visibility) {
                this.value.more_on_this_topic = [];
            }
        },
        "elements.table.visibility": function (visibility) {
            if (!visibility) {
                this.value.table = {};
            }
        },
        "elements.pros_and_cons.visibility": function (visibility) {
            if (!visibility) {
                this.value.pros_and_cons = null;
            }
        },
        "elements.how_to.visibility": function (visibility) {
            if (!visibility) {
                this.value.how_to = null;
            }

            this.$emit(visibility ? 'add-how-to' : 'delete-how-to', this.value.contentSectionUuid);
        },
        "elements.bonus_tiles.visibility": function (visibility) {
            if (!visibility) {
                this.value.bonus_tiles = [];
            }
        },
        "elements.category_tiles.visibility": function (visibility) {
            if (!visibility) {
                this.value.category_tiles = [];
            }
        },
        "elements.reference_list.visibility": function (visibility) {
            if (!visibility) {
                this.value.reference_list = {};
            }
        },
        value: function (newValue) {
            this.checkVisibility();
        },
    },
};
</script>
